import { useEffect, useMemo } from 'react';

import { type IdentifyParams, useCanny } from '~/providers/CannyProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { usePlanContext } from '~/providers/PlanProvider';
import { useWorkspaceMembersContext } from '~/providers/WorkspaceMembersProvider';
import { useCurrentWorkspaceMember } from '~/swr-hooks/members/useCurrentWorkspaceMember';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';

export const useCannyUrl = () => {
  const { canny, getCannyUrl } = useCanny();
  const { currentWorkspaceMember } = useCurrentWorkspaceMember();
  const { currentWorkspace } = useCurrentWorkspace();
  const { planMonthlyTotalPrice, planName } = usePlanContext();
  const { data: workspaceMembers } = useWorkspaceMembersContext();
  const { data: storage } = useStorage();

  const cannyParams = useMemo<Omit<IdentifyParams, 'appID'> | undefined>(() => {
    if (currentWorkspace && currentWorkspaceMember?.accountId) {
      return {
        user: {
          avatarURL: currentWorkspaceMember.avatar ?? '',
          email: currentWorkspaceMember.email,
          id: currentWorkspaceMember.accountId,
          name: `${currentWorkspaceMember.firstName} ${currentWorkspaceMember.lastName}`,
          companies: [
            {
              id: currentWorkspace.id,
              name: currentWorkspace.name,
              monthlySpend: planMonthlyTotalPrice ? planMonthlyTotalPrice / 100 : 0,
              customFields: {
                plan: planName,
                ['Total Workspace Members']: workspaceMembers?.length,
                ['Total Workspace Storage']: storage?.maxStorage,
              },
            },
          ],
        },
      };
    }
  }, [
    currentWorkspace,
    currentWorkspaceMember,
    planMonthlyTotalPrice,
    planName,
    storage?.maxStorage,
    workspaceMembers?.length,
  ]);

  const cannyUrl = useMemo(() => {
    return cannyParams ? getCannyUrl(cannyParams) : undefined;
  }, [cannyParams, getCannyUrl]);

  useEffect(() => {
    if (cannyParams) {
      canny?.identify(cannyParams);
    }
  }, [canny, cannyParams]);

  return {
    cannyUrl,
  };
};
